import { default as Modal } from 'antd/es/modal';
import { CountryFlag } from 'shared/src/CountryFlag';
import { CryptoFlag } from 'shared/src/CryptoFlag';
import { getCountryCode } from 'shared/src/getCountryCode';

import { inputMoneyFormatter } from 'utils/formatters/inputMoneyFormatter';
import { hasValue } from 'utils/hasValue';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import { ExchangeRateRefreshIndicator } from './ExchangeRateRefreshIndicator';
import type { useCryptoExchangeRate } from './useCryptoExchangeRate';

type Props = {
  isOpen: boolean;
  fromCurrency: string;
  toCurrency: string;
  amount: string;
  rateData: ReturnType<typeof useCryptoExchangeRate>['rateData'];
  secondsUntilExpiry: number | null;
  validitySeconds: number;
  isConfirming: boolean;
  isRateExpired: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const ExchangeConfirmationModal = ({
  isOpen,
  fromCurrency,
  toCurrency,
  amount,
  rateData,
  secondsUntilExpiry,
  validitySeconds,
  isConfirming,
  isRateExpired,
  onClose,
  onConfirm
}: Props) => {
  return (
    <Modal
      title={null}
      open={isOpen}
      onCancel={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton
            onClick={onConfirm}
            disabled={
              !hasValue(rateData?.quote_uuid) || isRateExpired || isConfirming
            }
          >
            <span className="inline-flex items-center gap-2">
              Confirm
              <ExchangeRateRefreshIndicator
                secondsUntilExpiry={secondsUntilExpiry}
                validitySeconds={validitySeconds}
              />
            </span>
          </PrimaryButton>
        </div>
      }
      width={400}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4">
          <p className="text-xl font-semibold text-content-primary">
            Confirm Exchange
          </p>
          <div className="flex items-center gap-2">
            <CryptoFlag cryptoCode={fromCurrency} className="size-8" />
            <span className="text-content-secondary">→</span>
            <CountryFlag
              className="!size-[32px]"
              countryCode={getCountryCode(toCurrency)}
              svg
            />
          </div>
          <p className="text-sm font-medium text-content-secondary">
            {fromCurrency} → {toCurrency}
          </p>
        </div>

        <div className="flex flex-col gap-4 px-2">
          <p className="text-base font-bold text-content-primary">
            Transaction details
          </p>

          <div className="flex flex-row justify-between gap-1">
            <span className="text-content-secondary">Amount</span>
            <span className="text-content-primary">
              {inputMoneyFormatter(amount)} {fromCurrency}
            </span>
          </div>

          <div className="flex flex-row justify-between gap-1">
            <span className="text-content-secondary">Exchange rate</span>
            <span className="text-content-primary">
              {hasValue(rateData?.quote_price) ? rateData.quote_price : '-'}
            </span>
          </div>

          <div className="flex flex-row justify-between gap-1">
            <span className="text-content-secondary">
              Estimated amount received
            </span>
            <span className="text-content-primary">
              {hasValue(rateData?.proceeds_after_fx_markup)
                ? `${rateData.proceeds_after_fx_markup} ${toCurrency}`
                : '-'}
            </span>
          </div>
        </div>

        <div className="rounded-lg bg-yellow-50 p-3 text-xs text-yellow-800">
          Please confirm that you want to proceed with this exchange.
        </div>
      </div>
    </Modal>
  );
};
